/**
 * get_field_config / 历史 cube_config_json.tables → Step2 fieldConfig。
 */
import type {
  IYamlFieldColumn,
  IYamlFieldColumnApi,
  IYamlFieldConfigTableApi,
  TStepFieldConfigPatch,
} from '../types';
import { buildEmptyCustomRowEdit } from './fieldConfigRowUtils';

/** 与 Step1「下一步」、编辑 retrieve 共用：单列对齐表格约定 */
export const mapFieldColumnsFromApiTable = (
  table: IYamlFieldConfigTableApi,
): IYamlFieldColumn[] => {
  const rawCols = (table?.columns || []) as IYamlFieldColumnApi[];
  return rawCols.map((c) => ({
    ...c,
    is_custom: Boolean(c.is_custom),
    table_id: table.table_id,
    table_name: table.table_name || table.table_id,
    is_primary_table: Boolean(table.is_primary),
    filters_text: c.default_kind === 'measure' ? '' : undefined,
  }));
};

/** `getFieldConfig` 响应 → Step2 patch；无表或无列时返回 null */
export const getFieldConfigPatch = (body: {
  tables?: unknown[];
}): TStepFieldConfigPatch | null => {
  const tables = body?.tables;
  if (!tables?.length) {
    return null;
  }
  const table = tables[0] as IYamlFieldConfigTableApi;
  const fieldColumns = mapFieldColumnsFromApiTable(table);
  if (!fieldColumns.length) {
    return null;
  }
  return {
    fieldLoading: false,
    fieldError: false,
    fieldsValue: fieldColumns.map((col) => col.source_column),
    fieldColumns,
    dimInlineOpen: false,
    measInlineOpen: false,
    dimAddDraft: buildEmptyCustomRowEdit('dimension'),
    measAddDraft: buildEmptyCustomRowEdit('metric'),
  };
};

const normalizeSavedFieldColumns = (
  columns: IYamlFieldColumn[],
  table?: IYamlFieldConfigTableApi,
): IYamlFieldColumn[] =>
  columns.map((col) => ({
    ...col,
    is_custom: Boolean(col.is_custom),
    table_id: col.table_id || table?.table_id || '',
    table_name:
      col.table_name || table?.table_name || table?.table_id || '',
    is_primary_table:
      col.is_primary_table ?? Boolean(table?.is_primary),
    filters_text:
      col.default_kind === 'measure'
        ? col.filters_text ?? ''
        : undefined,
  }));

/**
 * 解析 `cube_config_json`：优先 `fieldConfig`；否则用 `tables` 或 retrieve 的 tables 回退。
 */
export const cubeConfigPatch = (
  parsed: Record<string, unknown>,
  tablesFallback?: unknown[],
): TStepFieldConfigPatch => {
  const saved = parsed?.fieldConfig as TStepFieldConfigPatch | undefined;
  const parsedTables = parsed?.tables as unknown[] | undefined;
  const tables =
    parsedTables?.length ? parsedTables : tablesFallback;
  const tableFromLegacy =
    Array.isArray(tables) && tables.length
      ? (tables[0] as IYamlFieldConfigTableApi)
      : undefined;

  if (saved?.fieldColumns?.length) {
    const fieldColumns = normalizeSavedFieldColumns(
      saved.fieldColumns,
      tableFromLegacy,
    );
    return {
      ...saved,
      fieldColumns,
      fieldsValue:
        saved.fieldsValue?.length
          ? saved.fieldsValue
          : fieldColumns.map((col) => col.source_column),
      fieldLoading: false,
      fieldError: false,
    };
  }

  const fromTables = tableFromLegacy
    ? getFieldConfigPatch({ tables })
    : null;
  if (fromTables) {
    return {
      ...fromTables,
      fieldsValue:
        saved?.fieldsValue?.length
          ? saved.fieldsValue
          : fromTables.fieldsValue,
    };
  }

  return saved || {};
};
